import List from '@mui/material/List';
import ListItem, { ListItemProps } from '@mui/material/ListItem';
import { styledComponent } from '../../../../hoc';

/**
 * A styled version of the MUI `List` component.
 *
 * This component customizes padding for the list and its children. It ensures:
 * - No extra padding on the top or bottom of the list.
 * - Custom styles for child `ListItem` and `ListItemButton`.
 *
 * @example
 * ```tsx
 * import { ListComponent, ListItemComponent } from './ListComponents';
 *
 * const Example = () => (
 *   <ListComponent>
 *     <ListItemComponent onClick={() => console.log('Clicked!')}>
 *       Example Item
 *     </ListItemComponent>
 *   </ListComponent>
 * );
 * ```
 *
 * @category InformationCard
 */
export const ListComponent = styledComponent(List)(() => ({
  paddingTop: 0,
  paddingBottom: 0,

  '.MuiListItem-root': {
    padding: 0,
    marginBottom: 8,
    minHeight: 40,
  },

  '.MuiListItemButton-root': {
    padding: 0,
    marginBottom: 8,
    minHeight: 40,
  },
}));

/**
 * A styled version of the MUI `ListItem` component.
 *
 * This component adds conditional styling based on two factors:
 * 1. `onClick` handler: Adds padding, hover effects, and cursor styling.
 * 2. `hasBackground` prop: Applies a background color and hover effect.
 *
 * @param hasBackground - If true, applies a background color and primary text color.
 *
 * @example
 * ```tsx
 * <ListItemComponent
 *   hasBackground
 *   onClick={() => console.log('Clicked!')}
 * >
 *   Item with Background
 * </ListItemComponent>
 * ```
 *
 * @category InformationCard
 */
export const ListItemComponent = styledComponent<
  ListItemProps & { hasBackground?: boolean }
>(ListItem, { ignoreProps: ['hasBackground'] })(
  ({ theme, onClick, hasBackground }) => ({
    ...(onClick &&
      !hasBackground && {
        padding: '12px 16px !important',
        marginBottom: '0px !important',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.surfaceHover.onSurfaceVar,
        },
      }),

    ...(hasBackground && {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.surfaceContainer.container,
      cursor: 'pointer',

      '&:hover': {
        backgroundColor: theme.palette.surfaceHover.onSurfaceVar,
      },
    }),
  }),
);
